import { useState, useEffect } from 'react';
import { adminApi } from '../../lib/api';
import toast from 'react-hot-toast';

export default function AdminSettlementsPage() {
  const [batches, setBatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [running, setRunning] = useState(false);
  const [tab, setTab] = useState<'settlement' | 'tax-invoice' | 'charge' | 'calculator'>('settlement');

  const load = () => {
    setLoading(true);
    adminApi.settlements().then((res: any) => {
      setBatches(res.batches);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const runSettlement = async () => {
    if (!periodStart || !periodEnd) {
      toast.error('기간을 선택해주세요');
      return;
    }
    setRunning(true);
    try {
      const res: any = await adminApi.runSettlement(periodStart, periodEnd);
      toast.success(`정산 완료: ${res.reservations_processed}건, 총 ${new Intl.NumberFormat('ko-KR').format(res.total_amount)}원`);
      load();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setRunning(false);
    }
  };

  const formatPrice = (won: number) => new Intl.NumberFormat('ko-KR').format(won) + '원';

  return (
    <div>
      <h1 className="text-2xl font-bold mb-2">정산 및 세금계산서</h1>
      <p className="text-sm text-gray-500 mb-6">사후 정산, 세금계산서 발행, 카드 청구 관리</p>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-xl p-1 w-fit">
        {[
          { key: 'settlement', label: '정산 실행' },
          { key: 'calculator', label: '비용 계산기' },
          { key: 'tax-invoice', label: '세금계산서' },
          { key: 'charge', label: '카드 청구' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key as any)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-brand-500 ${
              tab === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Settlement Tab */}
      {tab === 'settlement' && (
        <div>
          <div className="card p- overflow-x-auto5 mb-6">
            <h2 className="font-semibold mb-3">정산 실행</h2>
            <p className="text-sm text-gray-500 mb-4">
              완료된 예약에 대해 정산을 실행합니다. 거래 수수료(15%)를 차감하고 차주 및 서비스 제공자에게 정산 금액을 배분합니다.
              정산 완료 후 세금계산서를 발행할 수 있습니다.
            </p>
            <div className="flex gap-3 items-end flex-wrap">
              <div>
                <label className="text-xs text-gray-500">시작일</label>
                <input type="date" className="input-field" value={periodStart} onChange={e => setPeriodStart(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-gray-500">종료일</label>
                <input type="date" className="input-field" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} />
              </div>
              <button onClick={runSettlement} disabled={running} className="bg-slate-900 text-white px-6 py-2.5 rounded-xl text-sm font-medium hover:bg-slate-800 disabled:opacity-50 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500">
                {running ? '실행 중...' : '정산 실행'}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600" /></div>
          ) : batches.length === 0 ? (
            <div className="card p- overflow-x-auto8 text-center text-gray-400">정산 내역이 없습니다</div>
          ) : (
            <div className="space-y-2">
              {batches.map((b: any) => (
                <div key={b.id} className="card p- overflow-x-auto4 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">{b.batch_id}</p>
                    <p className="text-xs text-gray-500">{b.period_start} ~ {b.period_end} · {b.recipient_count}명</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">{formatPrice(b.total_amount)}</p>
                    <span className={`badge text-xs ${b.status === 'completed' ? 'badge-green' : 'badge-gray'}`}>
                      {b.status === 'completed' ? '완료' : b.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Cost Calculator Tab */}
      {tab === 'calculator' && (
        <div>
          <div className="card p- overflow-x-auto5 mb-6">
            <h2 className="font-semibold mb-3">비용 계산기</h2>
            <p className="text-sm text-gray-500 mb-4">
              반납 완료된 예약의 최종 비용을 계산합니다. 대여료 + 케어 + 운송 + 손상 + 연체 등 모든 항목을 합산하여 최종 청구 금액을 산출합니다.
            </p>

            {/* Reservation selector */}
            <div className="mb-4">
              <label className="text-xs text-gray-500">예약 선택</label>
              <select className="input-field text-sm">
                <option value="">예약을 선택하세요</option>
                <option value="demo">[DEMO] A제작사 - 포르쉐 911 카브리올레 (6/5~6/8)</option>
              </select>
            </div>

            {/* Line items form */}
            <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 mb-4">
              {/* Rental */}
              <div className="p-3 flex items-center justify-between text-sm">
                <div>
                  <p className="font-medium text-slate-700">대여료</p>
                  <p className="text-xs text-gray-400">3일 × 450,000원</p>
                </div>
                <div className="text-right">
                  <input type="number" className="w-28 input-field text-sm text-right" defaultValue={1350000} />
                </div>
              </div>
              {/* Care */}
              <div className="p-3 flex items-center justify-between text-sm">
                <div>
                  <p className="font-medium text-slate-700">현장 케어</p>
                  <p className="text-xs text-gray-400">3일 × 150,000원</p>
                </div>
                <div className="text-right">
                  <input type="number" className="w-28 input-field text-sm text-right" defaultValue={450000} />
                </div>
              </div>
              {/* Transport */}
              <div className="p-3 flex items-center justify-between text-sm">
                <div>
                  <p className="font-medium text-slate-700">탁송료</p>
                  <p className="text-xs text-gray-400">서울→부산 400km</p>
                </div>
                <div className="text-right">
                  <input type="number" className="w-28 input-field text-sm text-right" defaultValue={350000} />
                </div>
              </div>
              {/* Damage */}
              <div className="p-3 flex items-center justify-between text-sm">
                <div>
                  <p className="font-medium text-slate-700">손상 복구</p>
                  <input type="text" className="input-field text-xs mt-1 w-full" placeholder="사유 입력" />
                </div>
                <div className="text-right">
                  <input type="number" className="w-28 input-field text-sm text-right" defaultValue={0} placeholder="0" />
                </div>
              </div>
              {/* Late fee */}
              <div className="p-3 flex items-center justify-between text-sm">
                <div>
                  <p className="font-medium text-slate-700">연체료</p>
                  <input type="text" className="input-field text-xs mt-1 w-full" placeholder="사유 입력" />
                </div>
                <div className="text-right">
                  <input type="number" className="w-28 input-field text-sm text-right" defaultValue={0} placeholder="0" />
                </div>
              </div>
              {/* Add custom line */}
              <div className="p-3">
                <button className="text-xs text-brand-600 hover:text-brand-700 font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500">+ 항목 추가</button>
              </div>
            </div>

            {/* Total */}
            <div className="bg-slate-900 text-white rounded-xl p-4 mb-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">최종 청구 금액</span>
                <span className="text-xl font-bold">{formatPrice(2150000)}</span>
              </div>
              <div className="text-xs text-slate-400 mt-2 space-y-0.5">
                <div className="flex justify-between"><span>공급가액</span><span>{formatPrice(1954500)}</span></div>
                <div className="flex justify-between"><span>부가세 (10%)</span><span>{formatPrice(195500)}</span></div>
              </div>
            </div>

            <div className="flex gap-2">
              <button className="flex-1 bg-slate-900 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-slate-800 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500">
                확정 및 세금계산서 발행
              </button>
              <button className="flex-1 bg-red-600 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500">
                빌링키 카드 청구
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tax Invoice Tab */}
      {tab === 'tax-invoice' && (
        <div>
          <div className="card p- overflow-x-auto5 mb-6">
            <h2 className="font-semibold mb-3">세금계산서 발행</h2>
            <p className="text-sm text-gray-500 mb-4">
              정산이 완료된 예약에 대해 세금계산서를 발행합니다.
              게스트(제작사)의 사업자등록번호로 발행되며, 이메일로 자동 발송됩니다.
            </p>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 mb-4">
              <p className="font-medium mb-1">세금계산서 발행 절차</p>
              <ol className="list-decimal list-inside space-y-1 text-amber-700">
                <li>예약 완료 → 정산 실행 → 금액 확정</li>
                <li>게스트 사업자등록번호 확인</li>
                <li>세금계산서 발행 (공급가 + 세액)</li>
                <li>이메일 발송 및 PDF 보관</li>
              </ol>
            </div>

            <div className="space-y-2">
              <p className="text-sm text-gray-400">발행 가능한 세금계산서 목록은 정산 완료 후 표시됩니다.</p>
              <div className="card p- overflow-x-auto6 text-center text-gray-400">
                <p>아직 발행 가능한 세금계산서가 없습니다</p>
                <p className="text-xs mt-1">먼저 정산을 실행해주세요</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Card Charge Tab */}
      {tab === 'charge' && (
        <div>
          <div className="card p- overflow-x-auto5 mb-6">
            <h2 className="font-semibold mb-3">카드 사후 청구</h2>
            <p className="text-sm text-gray-500 mb-4">
              대여 종료 후 추가 비용(손상, 연체, 추가 옵션 등)이 발생한 경우,
              사전에 등록된 빌링키로 카드 추가 청구를 진행할 수 있습니다.
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800 mb-4">
              <p className="font-medium mb-1">빌링키 사후 청구 조건</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li>게스트가 가입 시 빌링 동의를 한 경우에만 가능</li>
                <li>손상, 연체, 추가 옵션 등 실제 발생 비용만 청구</li>
                <li>청구 전 게스트에게 사전 고지 필수</li>
                <li>청구 내역은 정산 배치에 포함</li>
              </ul>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500">예약 ID</label>
                <input type="text" className="input-field" placeholder="예약 ID를 입력하세요" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500">청구 금액 (원)</label>
                  <input type="number" className="input-field" placeholder="100000" />
                </div>
                <div>
                  <label className="text-xs text-gray-500">청구 사유</label>
                  <select className="input-field">
                    <option value="">선택하세요</option>
                    <option value="damage">차량 손상</option>
                    <option value="late">연체료</option>
                    <option value="extra_option">추가 옵션</option>
                    <option value="other">기타</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500">상세 설명</label>
                <textarea className="input-field" rows={2} placeholder="청구 사유에 대한 상세 설명" />
              </div>
              <button className="bg-red-600 text-white px-6 py-2.5 rounded-xl text-sm font-medium hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500">
                카드 추가 청구
              </button>
            </div>
          </div>

          <div className="card p- overflow-x-auto5">
            <h3 className="font-semibold mb-3">사후 청구 내역</h3>
            <div className="card p- overflow-x-auto6 text-center text-gray-400">
              <p>아직 사후 청구 내역이 없습니다</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
